import { ApiError } from '@multiplatform-apps/service-trips';
import { isRouteErrorResponse } from 'react-router-dom';

export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof ApiError && error.status === 0) return true;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg === 'network error' || msg.includes('networkerror') || msg.includes('failed to fetch');
  }
  return false;
};

export const isUnauthorizedError = (error: unknown): boolean =>
  (error instanceof ApiError && error.status === 401) ||
  (isRouteErrorResponse(error) && error.status === 401);

export const isNotFoundError = (error: unknown): boolean =>
  (error instanceof ApiError && error.status === 404) ||
  (isRouteErrorResponse(error) && error.status === 404);

export const isForbiddenError = (error: unknown): boolean =>
  (error instanceof ApiError && error.status === 403) ||
  (isRouteErrorResponse(error) && error.status === 403);

export const isThrowableError = (error: unknown): boolean =>
  isNotFoundError(error) || isForbiddenError(error);

const SENSITIVE_KEYS = new Set([
  'token',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'authorization',
  'cookie',
  'password',
  'secret',
  'personnummer',
  'personalnumber',
  'sessionid',
]);

const sanitizeValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sanitizeValue);

  const sanitized: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      // key originates from Object.entries of the sanitized object
      // eslint-disable-next-line security/detect-object-injection
      sanitized[key] = '[REDACTED]';
    } else if (typeof val === 'object' && val !== null) {
      // key originates from Object.entries of the sanitized object
      // eslint-disable-next-line security/detect-object-injection
      sanitized[key] = sanitizeValue(val);
    } else {
      // key originates from Object.entries of the sanitized object
      // eslint-disable-next-line security/detect-object-injection
      sanitized[key] = val;
    }
  }
  return sanitized;
};

export const serializeError = (error: unknown): string => {
  try {
    if (error instanceof ApiError) {
      return JSON.stringify(
        {
          status: error.status,
          message: error.message,
          body: sanitizeValue(error.body),
        },
        null,
        2
      );
    }
    if (error instanceof Error) {
      return JSON.stringify(
        { name: error.name, message: error.message },
        null,
        2
      );
    }
    return JSON.stringify(sanitizeValue(error), null, 2);
  } catch {
    return String(error);
  }
};
